import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, Table, InputNumber, Input, Tag, message } from 'antd';
import {
    Consignment,
    ConsignmentItem,
    ConsignmentStockCheckPreviewRequest,
    ConsignmentStockCheckPreviewResult
} from '../../model/consignment';

interface Props {
    open: boolean;
    item: Consignment | null;
    onClose: () => void;
    /** V2: Backend's stock check is PRODUCT-aggregate across every active
     * Batch of the current OPEN Period -- the Website sends ONE counted
     * remaining per product_id, never a per-Batch count. FIFO attribution
     * of the resulting sold/correction across Batches is entirely
     * Backend-authoritative (see consignmentService.py::update_remaining).
     *
     * `countItems` holds only the products the user counted; a product
     * left blank is not in it. `expectedPeriodId` is the Period this modal
     * observed, the same one every Preview was run against. */
    onSave: (
        consignmentId: number,
        countItems: { product_id: number; latest_actual_remaining: number }[],
        note?: string,
        expectedPeriodId?: number
    ) => Promise<void>;
    /** Called after Backend refused the Save because the Period changed
     * under the modal (STALE_PERIOD / ALREADY_CLOSED): nothing was saved,
     * and the caller reloads the bill. */
    onStale?: () => void;
    /** Backend-authoritative STOCK_CHECK_PREVIEW -- read-only, zero side
     * effects, uses the EXACT SAME FIFO/correction engine UPDATE_REMAINING
     * uses to persist (see consignmentService.py::_simulate_stock_check).
     * The Website must never compute this itself. */
    onPreview: (consignmentId: number, payload: ConsignmentStockCheckPreviewRequest) => Promise<ConsignmentStockCheckPreviewResult>;
}

interface ProductGroup {
    product_id: number;
    product_name?: string;
    product_code?: string;
    product_unit_name?: string;
    /** All ACTIVE Batches of this product in the current Period -- shown
     * read-only so the user can see composition, never edited individually. */
    batches: ConsignmentItem[];
    system_remaining: number;
    max_possible: number;
    counted: number | undefined;
}

/** 300ms -- matches this project's existing debounce convention (see
 * common-search.tsx), within the 250-400ms range asked for here. */
const PREVIEW_DEBOUNCE_MS = 300;

const STALE_OR_CLOSED_MESSAGE = 'รอบบิลนี้มีการเปลี่ยนแปลงแล้ว กรุณารีเฟรชข้อมูลก่อนดำเนินการอีกครั้ง';

export const NOTHING_COUNTED_MESSAGE = 'กรุณาระบุจำนวนที่ตรวจนับอย่างน้อย 1 รายการ';

/** A row the user typed a number into. Blank means "not counted in this
 * check" -- it is neither 0 nor the system's number, and it is never sent. */
const isEntered = (g: { counted: number | undefined }) => g.counted !== undefined && g.counted !== null;

const sourceTypeLabel = (sourceType?: string) => {
    switch (sourceType) {
        case 'INITIAL': return 'ฝากเริ่มต้น';
        case 'ADD_STOCK': return 'ฝากเพิ่ม';
        case 'CARRY_FORWARD': return 'ยกยอดจากรอบก่อน';
        default: return sourceType || '-';
    }
};

const formatMoney = (val: number | undefined): string =>
    val === undefined ? '-' : `฿${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export const ConsignmentCheckModal: React.FC<Props> = ({
    open,
    item,
    onClose,
    onSave,
    onPreview,
    onStale
}) => {
    const [groups, setGroups] = useState<ProductGroup[]>([]);
    const [note, setNote] = useState<string>('');
    const [loading, setLoading] = useState(false);

    // Backend-authoritative preview state -- null means "no current
    // preview" (nothing entered yet, cleared on error, or reset on open),
    // never a fabricated/estimated value.
    const [preview, setPreview] = useState<ConsignmentStockCheckPreviewResult | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Incremented once per actual STOCK_CHECK_PREVIEW call issued (not per
    // keystroke). Comparing against this on resolution discards any
    // response that is no longer the latest in-flight request, regardless
    // of network completion order (out-of-order response protection).
    const latestPreviewRequestIdRef = useRef(0);
    const isMountedRef = useRef(true);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    useEffect(() => {
        if (open && item) {
            const activeBatches = (item.items || []).filter(it => (it.item_status || 'ACTIVE') === 'ACTIVE');
            const byProduct = new Map<number, ConsignmentItem[]>();
            activeBatches.forEach(b => {
                const list = byProduct.get(b.product_id) || [];
                list.push(b);
                byProduct.set(b.product_id, list);
            });
            const nextGroups: ProductGroup[] = Array.from(byProduct.entries()).map(([product_id, batches]) => {
                const system_remaining = batches.reduce((sum, b) => sum + Number(b.latest_actual_remaining || 0), 0);
                const max_possible = batches.reduce(
                    (sum, b) => sum + Math.max(0, Number(b.quantity_received || 0) - Number(b.quantity_returned || 0) - Number(b.quantity_carried_forward || 0)),
                    0
                );
                return {
                    product_id,
                    product_name: batches[0]?.product_name,
                    product_code: batches[0]?.product_code,
                    product_unit_name: batches[0]?.product_unit_name,
                    batches,
                    system_remaining,
                    max_possible,
                    counted: undefined
                };
            });
            setGroups(nextGroups);
            setNote('');
            setPreview(null);
            setPreviewError(null);
            setPreviewLoading(false);
            latestPreviewRequestIdRef.current += 1; // invalidate any in-flight request from a previous open
        }
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, [open, item]);

    // The Period the caller actually observed as OPEN (from GET, before
    // this modal opened) -- never re-derived/assumed while the modal is
    // open. Same value used consistently for every Preview call.
    const expectedPeriodId = item?.current_period?.id;

    // Same product_id/latest_actual_remaining shape sent to both Preview
    // and Save -- only rows with a currently-valid entered integer >= 0 are
    // included, so a decimal/empty row never gets silently treated as 0.
    const buildPreviewItems = useCallback((gs: ProductGroup[]) => {
        return gs
            .filter(g => g.counted !== undefined && g.counted !== null && Number.isInteger(g.counted) && g.counted >= 0)
            .map(g => ({ product_id: g.product_id, latest_actual_remaining: Number(g.counted) }));
    }, []);

    const runPreview = useCallback((gs: ProductGroup[]) => {
        if (!item || expectedPeriodId === undefined || expectedPeriodId === null) {
            setPreview(null);
            setPreviewError(null);
            return;
        }
        const items = buildPreviewItems(gs);
        if (items.length === 0) {
            // Nothing valid entered -- do not call Backend, reset to "no preview".
            latestPreviewRequestIdRef.current += 1;
            setPreview(null);
            setPreviewError(null);
            setPreviewLoading(false);
            return;
        }

        const requestId = ++latestPreviewRequestIdRef.current;
        setPreviewLoading(true);
        onPreview(item.id, { expected_period_id: expectedPeriodId, items })
            .then(result => {
                if (!isMountedRef.current || requestId !== latestPreviewRequestIdRef.current) return;
                setPreview(result);
                setPreviewError(null);
            })
            .catch(err => {
                if (!isMountedRef.current || requestId !== latestPreviewRequestIdRef.current) return;
                const msg: string = err?.message || 'ไม่สามารถดูตัวอย่างผลตรวจนับได้';
                setPreview(null);
                if (msg.includes('STALE_PERIOD') || msg.includes('ALREADY_CLOSED')) {
                    // Do NOT retry automatically against whatever Period is
                    // now open -- surface it and let the user refresh.
                    setPreviewError(STALE_OR_CLOSED_MESSAGE);
                } else {
                    setPreviewError(msg);
                }
            })
            .finally(() => {
                if (!isMountedRef.current || requestId !== latestPreviewRequestIdRef.current) return;
                setPreviewLoading(false);
            });
    }, [item, expectedPeriodId, onPreview, buildPreviewItems]);

    const handleCountChange = (productId: number, val: number | undefined) => {
        setGroups(prev => {
            const next = prev.map(g => g.product_id === productId ? { ...g, counted: val } : g);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => runPreview(next), PREVIEW_DEBOUNCE_MS);
            return next;
        });
    };

    // Local, non-authoritative display-only aggregates -- plain sums of
    // what's on screen, not FIFO-derived, so they need no Backend round
    // trip and stay in sync with every keystroke instantly.
    //
    // Both sums cover ONLY the products being counted -- the same rows
    // Preview and Save send. The system total of every product in the
    // Period next to the counted total of some of them compared nothing.
    const localTotals = useMemo(() => {
        const counting = new Set(buildPreviewItems(groups).map(i => i.product_id));
        let countedSystem = 0;
        let countedTotal = 0;
        let enteredCount = 0;
        groups.forEach(g => {
            if (isEntered(g)) enteredCount += 1;
            if (counting.has(g.product_id)) {
                countedSystem += g.system_remaining;
                countedTotal += Number(g.counted);
            }
        });
        return { countedSystem, countedTotal, enteredCount, skippedCount: groups.length - enteredCount, countingCount: counting.size };
    }, [groups, buildPreviewItems]);

    const backendTotals = preview?.totals;

    if (!item) return null;

    const handleSubmit = async () => {
        // A partial count is a normal count: only the rows the user filled
        // in are checked and sent. A blank row is "not counted this time" --
        // never sent as 0 or as the system's number, which would record a
        // count nobody made (STOCK_CHECK history, and the lot's edit/void
        // guard would treat it as counted).
        const entered = groups.filter(isEntered);
        if (entered.length === 0) {
            message.error(NOTHING_COUNTED_MESSAGE);
            return;
        }
        for (const g of entered) {
            const counted = Number(g.counted);
            if (!Number.isInteger(counted)) {
                message.error(`จำนวนคงเหลือจริงของ "${g.product_name}" ต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม)`);
                return;
            }
            if (counted < 0 || counted > g.max_possible) {
                message.error(`จำนวนคงเหลือของ "${g.product_name}" ต้องอยู่ระหว่าง 0 ถึง ${g.max_possible}`);
                return;
            }
        }

        // The same rows Preview sent -- every entered row is valid by now.
        const payloadItems = buildPreviewItems(groups);
        try {
            setLoading(true);
            await onSave(item.id, payloadItems, note, expectedPeriodId);
            setLoading(false);
            onClose();
        } catch (e: any) {
            setLoading(false);
            const msg: string = e?.message || 'บันทึกการตรวจนับไม่สำเร็จ';
            if (msg.includes('STALE_PERIOD') || msg.includes('ALREADY_CLOSED')) {
                // The Period changed under the modal and Backend saved
                // nothing. Say so, and reload rather than retry into
                // whatever Period is open now.
                message.error(STALE_OR_CLOSED_MESSAGE);
                onClose();
                onStale?.();
                return;
            }
            message.error(msg);
        }
    };

    const columns = [
        {
            title: '#',
            width: 40,
            render: (_: any, __: any, index: number) => index + 1
        },
        {
            title: 'สินค้า',
            dataIndex: 'product_name',
            key: 'product_name',
            width: 220,
            render: (_: any, r: ProductGroup) => (
                <div>
                    <div style={{ fontWeight: 700 }}>{r.product_name}</div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>รหัส: {r.product_code || '-'}</div>
                </div>
            )
        },
        {
            title: 'ล็อตในงวดนี้',
            key: 'batches',
            width: 220,
            render: (_: any, r: ProductGroup) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {r.batches.map(b => (
                        <span key={b.id} style={{ fontSize: 11, color: '#64748B' }}>
                            <Tag style={{ marginRight: 4 }}>#{b.batch_no}</Tag>
                            {sourceTypeLabel(b.source_type)} · เหลือ {b.latest_actual_remaining}
                        </span>
                    ))}
                </div>
            )
        },
        {
            title: 'ระบบคงเหลือรวม',
            dataIndex: 'system_remaining',
            key: 'system_remaining',
            width: 110,
            align: 'right' as const,
            render: (val: number, r: ProductGroup) => `${val} ${r.product_unit_name || ''}`
        },
        {
            title: 'นับได้จริง',
            key: 'counted',
            width: 140,
            render: (_: any, r: ProductGroup) => (
                <InputNumber
                    // No min/max here on purpose: AntD's InputNumber silently
                    // drops onChange (never updates React state) when a typed
                    // value falls outside min/max, but leaves the native
                    // <input> DOM node showing whatever was typed -- so the
                    // visible field, the Preview, and the Save payload could
                    // diverge with no error shown. Range (0 to max_possible)
                    // is enforced explicitly and visibly in handleSubmit
                    // instead, so the input state is always the single
                    // source of truth for what's visible/previewed/submitted.
                    precision={0}
                    // Not "0": a blank field means "not counted", and a
                    // grey 0 in it read as a count of zero.
                    placeholder="ไม่ได้นับ"
                    style={{ width: '100%', fontWeight: 700 }}
                    value={r.counted}
                    onChange={(v) => handleCountChange(r.product_id, v === null ? undefined : Number(v))}
                    data-testid={`input-remaining-${r.product_id}`}
                />
            )
        },
        {
            title: 'ขายได้',
            key: 'sold',
            width: 110,
            align: 'center' as const,
            render: (_: any, r: ProductGroup) => {
                if (r.counted === undefined) return <span className="consignment-check-muted">-</span>;
                const previewItem = preview?.items.find(i => i.product_id === r.product_id);
                if (!previewItem) {
                    // Entered but not yet confirmed by Backend (debounce
                    // pending / in flight / failed) -- never guess a value.
                    return <span className="consignment-check-muted" data-testid={`preview-pending-${r.product_id}`}>…</span>;
                }
                if (previewItem.sold_quantity > 0) {
                    return <strong className="consignment-cell-sold">{previewItem.sold_quantity}</strong>;
                }
                if (previewItem.correction_quantity > 0) {
                    return (
                        <span
                            className="consignment-check-correction"
                            title="นับได้มากกว่าระบบคงเหลือ — เป็นการปรับเพิ่มยอดนับ ไม่ใช่ยอดขาย"
                        >
                            ปรับเพิ่ม +{previewItem.correction_quantity}
                        </span>
                    );
                }
                return <strong className="consignment-check-muted">0</strong>;
            }
        }
    ];

    return (
        <Modal
            open={open}
            title={`📝 ตรวจนับสต๊อกคงเหลือจริง: ${item.document_no} (${item.vendor_name || 'ผู้ฝากขาย'})`}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText="💾 บันทึกผลการตรวจนับ"
            cancelText="ยกเลิก"
            width={920}
            destroyOnClose
        >
            <div style={{ marginBottom: '12px', color: '#64748B', fontSize: '13px' }}>
                กรอก <strong>"จำนวนคงเหลือจริงที่นับได้"</strong> ของสินค้าแต่ละตัว <strong>รวมทุกล็อตในงวดปัจจุบัน</strong> ไม่ต้องแยกนับทีละล็อต
                ระบบหลังบ้านจะคำนวณยอดขายและจัดสรรให้แต่ละล็อตโดยอัตโนมัติตามลำดับล็อตเก่าสุดก่อน (FIFO)
                <div style={{ marginTop: 6 }} data-testid="check-partial-hint">
                    <strong>กรอกเฉพาะสินค้าที่ตรวจนับจริง ช่องที่เว้นว่างจะไม่ถูกบันทึกและไม่เปลี่ยนข้อมูล</strong>
                    {' · '}ถ้าตรวจแล้วสินค้าเหลือ 0 ให้กรอก 0
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={groups.map((g, i) => ({ ...g, key: g.product_id || i }))}
                pagination={false}
                size="small"
                bordered
                style={{ marginBottom: '16px' }}
            />

            {/* Authoritative Backend preview -- system/counted totals are
                plain local sums (display only); sold/correction/sales/
                payable/profit come from STOCK_CHECK_PREVIEW, the exact same
                FIFO/correction engine UPDATE_REMAINING uses to persist. */}
            <div className="consignment-calc-box" style={{ marginBottom: '14px' }}>
                <div className="consignment-calc-title">
                    <span>ตัวอย่างก่อนบันทึก</span>
                    {previewLoading && (
                        <span className="consignment-check-muted" style={{ fontWeight: 400, fontSize: 11 }} data-testid="preview-loading">
                            ⏳ กำลังคำนวณ...
                        </span>
                    )}
                </div>
                <div className="consignment-check-progress" data-testid="check-progress">
                    ตรวจนับแล้ว {localTotals.enteredCount} / {groups.length} รายการ · ข้าม {localTotals.skippedCount} รายการ
                </div>
                <div className="consignment-calc-grid">
                    {/* Scoped to the products being counted in this check:
                        what the system holds for them, and what was found. */}
                    <div className="consignment-calc-item">
                        <span className="calc-label">ระบบคงเหลือ (เฉพาะที่ตรวจ):</span>
                        <span className="calc-val" data-testid="summary-system-counted">
                            {localTotals.countingCount > 0 ? `${localTotals.countedSystem} ชิ้น` : '-'}
                        </span>
                    </div>
                    <div className="consignment-calc-item">
                        <span className="calc-label">นับได้จริง (เฉพาะที่ตรวจ):</span>
                        <span className="calc-val" data-testid="summary-counted">
                            {localTotals.countingCount > 0 ? `${localTotals.countedTotal} ชิ้น` : '-'}
                        </span>
                    </div>
                    <div className="consignment-calc-item">
                        <span className="calc-label">ขายได้รวม:</span>
                        <span className="calc-val sold">{backendTotals ? `${backendTotals.sold_quantity} ชิ้น` : '-'}</span>
                    </div>
                    <div className="consignment-calc-item">
                        <span className="calc-label">ยอดขาย:</span>
                        <span className="calc-val sales" data-testid="preview-sales-amount">{formatMoney(backendTotals?.sales_amount)}</span>
                    </div>
                    <div className="consignment-calc-item">
                        <span className="calc-label">ค้างจ่ายผู้ฝาก:</span>
                        <span className="calc-val payable" data-testid="preview-owner-payable">{formatMoney(backendTotals?.owner_payable)}</span>
                    </div>
                    <div className="consignment-calc-item">
                        <span className="calc-label">กำไรร้าน:</span>
                        <span className="calc-val profit" data-testid="preview-shop-profit">{formatMoney(backendTotals?.shop_profit)}</span>
                    </div>
                    {backendTotals && backendTotals.correction_quantity > 0 && (
                        <div className="consignment-calc-item">
                            <span className="calc-label">ปรับเพิ่มรวม:</span>
                            <span className="calc-val correction">+{backendTotals.correction_quantity} ชิ้น</span>
                        </div>
                    )}
                </div>
                {previewError ? (
                    <div className="consignment-preview-error" data-testid="preview-error">
                        {previewError}
                    </div>
                ) : (
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 6 }}>
                        ยอดขาย, ยอดค้างจ่าย และกำไรคำนวณโดย Backend (FIFO จริง) เหมือนกับตอนบันทึกทุกประการ
                    </div>
                )}
            </div>

            <div>
                <span style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                    หมายเหตุการตรวจนับ (Note):
                </span>
                <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="เช่น ตรวจนับรอบบ่าย, เติมของเพิ่ม, ขนมหมดเร็ว..."
                    data-testid="input-check-note"
                />
            </div>
        </Modal>
    );
};

export default ConsignmentCheckModal;
